import { type Scenario, emptyScenario, parseScenarioFile } from '@orgsim/shared';
import { useEffect, useState } from 'react';
import { ApiError, OfflineError } from '../api/client';
import {
  type RunSummary,
  deleteRun as deleteRemoteRun,
  getRun as getRemoteRun,
  listRuns as listRemoteRuns,
  putRun as putRemoteRun,
} from '../api/runs';
import { type StoredScenario, deleteScenario, getScenario, listScenarios } from '../api/scenarios';
import { useSession } from '../api/session';
import { DEMO_SCENARIO } from '../demo';
import { DrawingLayer } from '../map/DrawingLayer';
import { MapView } from '../map/MapView';
import { PatientMarker } from '../map/PatientMarker';
import { SignMarker } from '../map/SignMarker';
import { useAppStore } from '../store/appStore';
import {
  type RunRecord,
  dbAvailable,
  deleteRun as deleteLocalRun,
  listRuns as listLocalRuns,
  listLocalScenarios,
} from '../store/db';
import { useRunStore } from '../store/runStore';
import {
  type ListedRun,
  eventCountOf,
  finishedUploads,
  holdSync,
  loadListedRun,
  mergeRuns,
  syncRun,
} from '../store/sync';
import { Icon } from './Icon';
import { pickTextFile } from './download';

const MODE_LABELS = { orgl: 'OrgL', lna: 'LNA', both: 'OrgL + LNA' };

// Server and API errors already carry a German message; anything else (IndexedDB) does not.
const messageOf = (e: unknown, fallback: string) =>
  e instanceof ApiError || e instanceof OfflineError ? e.message : fallback;

const scenarioNameOf = (run: ListedRun) => ('events' in run ? run.scenario.name : run.scenarioName);

export function StartScreen() {
  const [serverScenarios, setServerScenarios] = useState<StoredScenario[]>([]);
  const [localScenarios, setLocalScenarios] = useState<Scenario[]>([]);
  // null until the server's run list is known; stays null if it could not be loaded.
  const [remoteRuns, setRemoteRuns] = useState<RunSummary[] | null>(null);
  const [localRuns, setLocalRuns] = useState<RunRecord[]>([]);
  const [storage, setStorage] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'scenarios' | 'runs' | null>(null);
  const [query, setQuery] = useState('');
  const [mode, setMode] = useState('all');
  const [selectedId, setSelectedId] = useState(DEMO_SCENARIO.id);
  const openEditor = useAppStore((s) => s.openEditor);
  const openRun = useAppStore((s) => s.openRun);
  const openUsers = useAppStore((s) => s.openUsers);
  const startRun = useRunStore((s) => s.start);
  const resumeRun = useRunStore((s) => s.resume);
  const imported = useAppStore((s) => s.imported);
  const addImported = useAppStore((s) => s.addImported);
  const removeImported = useAppStore((s) => s.removeImported);
  const user = useSession((s) => s.user);
  const logout = useSession((s) => s.logout);
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    const runsFromServer = async () => {
      try {
        const runs = await listRemoteRuns();
        setRemoteRuns(runs);
        return runs;
      } catch (e) {
        setError(messageOf(e, 'Durchführungen konnten nicht geladen werden.'));
        return null;
      }
    };
    const scenariosFromServer = async () => {
      try {
        const entries = await listScenarios();
        // One request per scenario for the full document. Fine for a few dozen
        // scenarios; have the list route return documents if the library grows.
        // A scenario deleted between list and fetch is simply left out.
        const results = await Promise.allSettled(entries.map((e) => getScenario(e.id)));
        setServerScenarios(results.flatMap((r) => (r.status === 'fulfilled' ? [r.value] : [])));
      } catch (e) {
        setError(messageOf(e, 'Szenarien konnten nicht geladen werden.'));
      }
    };
    const fromBrowser = async () => {
      try {
        if (!(await dbAvailable())) {
          setStorage(false);
          return [];
        }
        const [scenarios, runs] = await Promise.all([listLocalScenarios(), listLocalRuns()]);
        setLocalScenarios(scenarios);
        setLocalRuns(runs);
        return runs;
      } catch {
        setStorage(false);
        return [];
      }
    };
    // A finished run whose last upload failed (network, expired session) would otherwise reach
    // the server only when someone opens its protocol.
    const uploadFinished = async (remote: RunSummary[] | null, local: RunRecord[]) => {
      const user = useSession.getState().user;
      if (!remote || !user) return;
      const uploads = finishedUploads(local, remote, user.id);
      if (uploads.length === 0) return;
      const results = await Promise.allSettled(uploads.map(putRemoteRun));
      if (results.some((r) => r.status === 'rejected')) {
        setError('Abgeschlossene Durchführungen konnten nicht auf den Server übertragen werden.');
      }
      try {
        setRemoteRuns(await listRemoteRuns());
      } catch {
        // The list from before stays; the merge still shows the local copies.
      }
    };
    Promise.all([runsFromServer(), fromBrowser(), scenariosFromServer()]).then(
      async ([remote, local]) => {
        setLoading(false);
        await uploadFinished(remote, local);
      },
    );
  }, []);

  const importFile = async () => {
    const text = await pickTextFile();
    if (!text) return;
    try {
      const scenario = parseScenarioFile(text);
      addImported(scenario);
      setSelectedId(scenario.id);
      setQuery('');
      setMode('all');
      setView('scenarios');
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const begin = async (scenario: Scenario) => {
    await startRun(scenario);
    openRun();
  };
  const resume = async (run: ListedRun) => {
    let record: RunRecord;
    try {
      record = await loadListedRun(run, getRemoteRun);
    } catch (e) {
      setError(messageOf(e, 'Durchführung konnte nicht geladen werden.'));
      return;
    }
    resumeRun(record);
    // Server state unknown: sending the local copy could overwrite a newer one, so hold it
    // back for this session. Otherwise, a local copy that won the merge is uploaded.
    if (remoteRuns === null) holdSync(record.id);
    else if ('events' in run) syncRun(record);
    openRun();
  };
  const deleteRun = async (run: ListedRun) => {
    if (!window.confirm('Durchführung löschen?')) return;
    try {
      await deleteRemoteRun(run.id);
      if (storage) await deleteLocalRun(run.id);
      setRemoteRuns((prev) => (prev ? prev.filter((x) => x.id !== run.id) : null));
      setLocalRuns((prev) => prev.filter((x) => x.id !== run.id));
    } catch (e) {
      setError(messageOf(e, 'Durchführung konnte nicht gelöscht werden.'));
    }
  };

  // An import shadows the server copy for this session; the server copy shadows leftovers
  // that older versions saved in this browser.
  const importedIds = new Set(imported.map((s) => s.id));
  const serverById = new Map(serverScenarios.map((s) => [s.id, s]));
  const scenarios = [
    ...imported,
    ...serverScenarios.map((s) => s.document).filter((s) => !importedIds.has(s.id)),
    ...localScenarios.filter((s) => !importedIds.has(s.id) && !serverById.has(s.id)),
  ];
  const originOf = (id: string) => {
    if (importedIds.has(id)) return 'importiert, nicht auf dem Server';
    const stored = serverById.get(id);
    if (stored) return stored.published ? null : 'Entwurf, für Ausbilder nicht sichtbar';
    if (localScenarios.some((s) => s.id === id)) return 'lokal, nicht auf dem Server';
    return null;
  };
  const deleteSelected = async (scenario: Scenario) => {
    if (!window.confirm(`„${scenario.name}“ löschen?`)) return;
    if (importedIds.has(scenario.id)) {
      removeImported(scenario.id);
      return;
    }
    try {
      await deleteScenario(scenario.id);
      setServerScenarios((prev) => prev.filter((s) => s.id !== scenario.id));
    } catch (e) {
      setError(messageOf(e, 'Szenario konnte nicht gelöscht werden.'));
    }
  };
  const runs = mergeRuns(localRuns, remoteRuns ?? []);
  const ownerOf = new Map((remoteRuns ?? []).map((r) => [r.id, r.userId]));
  const isForeign = (run: ListedRun) => {
    const owner = ownerOf.get(run.id);
    return owner !== undefined && owner !== user?.id;
  };
  const library = scenarios.some((s) => s.id === DEMO_SCENARIO.id)
    ? scenarios
    : [...scenarios, DEMO_SCENARIO];
  const filtered = library.filter(
    (s) =>
      (mode === 'all' || s.mode === mode || s.mode === 'both') &&
      `${s.name} ${s.situation.keyword}`
        .toLocaleLowerCase('de')
        .includes(query.toLocaleLowerCase('de')),
  );
  const selected = library.find((s) => s.id === selectedId) ?? library[0];
  const isExample =
    selected?.id === DEMO_SCENARIO.id && !scenarios.some((s) => s.id === selected.id);
  const activeRuns = runs.filter((r) => !r.finishedAt && !isForeign(r));
  const latestRun = activeRuns[0];
  const selectedOrigin = selected ? originOf(selected.id) : null;
  const canDelete =
    selected && (importedIds.has(selected.id) || (isAdmin && serverById.has(selected.id)));

  return (
    <div className="mission-launcher">
      {selected ? (
        <div className="launch-map">
          <MapView
            key={selected.id}
            center={selected.situation.location}
            zoom={5}
            location={selected.situation.location}
          >
            {selected.units.map((u) =>
              u.position ? (
                <SignMarker
                  key={u.id}
                  sign={{
                    id: u.id,
                    signType: u.type,
                    position: u.position,
                    label: u.callsign,
                    organisation: u.organisation,
                  }}
                  draggable={false}
                />
              ) : null,
            )}
            {selected.patients.map((p) => (
              <PatientMarker key={p.id} patient={p} />
            ))}
            <DrawingLayer drawings={selected.drawings} />
          </MapView>
        </div>
      ) : null}
      <header className="simulation-header launch-header">
        <div className="brand">
          <Icon name="map" size={23} />
          <span>OrgSim</span>
        </div>
        <span className="header-divider" />
        <button
          type="button"
          className="scenario-switch"
          aria-label="Szenario wechseln"
          aria-expanded={view === 'scenarios'}
          onClick={() => setView(view === 'scenarios' ? null : 'scenarios')}
        >
          <Icon name="map" size={16} />
          <span>{selected?.name ?? 'Szenario auswählen'}</span>
          <Icon name="chevron" size={14} style={{ rotate: '90deg' }} />
        </button>
        <div className="button-group header-actions">
          <button
            type="button"
            className="button quiet"
            aria-pressed={view === 'runs'}
            onClick={() => setView(view === 'runs' ? null : 'runs')}
          >
            <Icon name="history" size={17} />
            Durchführungen
            {activeRuns.length ? <span className="nav-count">{activeRuns.length}</span> : null}
          </button>
          {isAdmin ? (
            <button
              type="button"
              className="button quiet"
              onClick={() => openEditor(emptyScenario(crypto.randomUUID()))}
            >
              <Icon name="plus" size={17} />
              <span>Neues Szenario</span>
            </button>
          ) : null}
          {isAdmin ? (
            <button type="button" className="button quiet" onClick={openUsers}>
              <Icon name="people" size={17} />
              <span>Benutzer</span>
            </button>
          ) : null}
          {user ? (
            <>
              <span className="header-divider" />
              <span className="muted">{user.displayName}</span>
              <button type="button" className="button quiet" onClick={logout}>
                Abmelden
              </button>
            </>
          ) : null}
        </div>
      </header>
      {!storage || error ? (
        <div className="launch-warning notice warning" role="alert">
          {error ?? 'Lokale Speicherung ist gesperrt. Änderungen gehen beim Schließen verloren.'}
        </div>
      ) : null}
      {loading ? <output className="launch-loading">Szenarien werden geladen …</output> : null}
      {view ? (
        <section
          className="launch-picker"
          aria-label={view === 'scenarios' ? 'Szenarioauswahl' : 'Gespeicherte Durchführungen'}
        >
          <div className="drawer-heading">
            <h1>{view === 'scenarios' ? 'Szenarien' : 'Durchführungen'}</h1>
            <button
              type="button"
              className="button quiet icon-button"
              aria-label="Auswahl schließen"
              onClick={() => setView(null)}
            >
              <Icon name="close" />
            </button>
          </div>
          <div className="launch-picker-content">
            {view === 'scenarios' ? (
              <>
                <div className="library-filters">
                  <div className="search-field">
                    <Icon name="search" size={17} />
                    <input
                      aria-label="Szenarien suchen"
                      placeholder="Szenario suchen …"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                    />
                  </div>
                  <select
                    aria-label="Prüfungsmodus filtern"
                    value={mode}
                    onChange={(e) => setMode(e.target.value)}
                  >
                    <option value="all">Alle Prüfungsmodi</option>
                    <option value="orgl">OrgL</option>
                    <option value="lna">LNA</option>
                  </select>
                </div>
                <ul className="scenario-list">
                  {filtered.map((s) => (
                    <li key={s.id}>
                      <button
                        type="button"
                        className={
                          selected?.id === s.id ? 'scenario-option selected' : 'scenario-option'
                        }
                        aria-pressed={selected?.id === s.id}
                        onClick={() => {
                          setSelectedId(s.id);
                          setView(null);
                        }}
                      >
                        <Icon name="map" size={21} />
                        <span className="scenario-option-copy">
                          <strong>{s.name}</strong>
                          <span>
                            {s.situation.keyword || 'Ohne Stichwort'} · {MODE_LABELS[s.mode]}
                          </span>
                          <span>
                            {s.patients.length} Patienten · {s.units.length} Kräfte
                          </span>
                          {originOf(s.id) ? <span>{originOf(s.id)}</span> : null}
                        </span>
                        <Icon name="chevron" size={15} />
                      </button>
                    </li>
                  ))}
                </ul>
                {filtered.length === 0 ? (
                  <div className="empty-state">
                    <p>Keine passenden Szenarien.</p>
                    <button
                      type="button"
                      className="button"
                      onClick={() => {
                        setQuery('');
                        setMode('all');
                      }}
                    >
                      Filter zurücksetzen
                    </button>
                  </div>
                ) : null}
              </>
            ) : runs.length === 0 ? (
              <div className="empty-state">
                <Icon name="history" size={26} />
                <p>Noch keine Übungen begonnen.</p>
              </div>
            ) : (
              <ul className="saved-run-list">
                {runs.map((r) => (
                  <li key={r.id}>
                    <div>
                      <strong>{scenarioNameOf(r)}</strong>
                      <span>
                        {new Date(r.startedAt).toLocaleString('de-DE', {
                          dateStyle: 'short',
                          timeStyle: 'short',
                        })}{' '}
                        · {eventCountOf(r)} Ereignisse
                      </span>
                    </div>
                    <div className="button-group">
                      {/* Continuing someone else's open run would only be refused on save. */}
                      {r.finishedAt || !isForeign(r) ? (
                        <button type="button" className="button" onClick={() => resume(r)}>
                          {r.finishedAt ? 'Protokoll ansehen' : 'Fortsetzen'}
                          <Icon name="chevron" size={14} />
                        </button>
                      ) : null}
                      {/* A finished run is the exam record; only an admin may delete it. */}
                      {isAdmin || !r.finishedAt ? (
                        <button
                          type="button"
                          className="button quiet icon-button danger-subtle"
                          aria-label={`Durchführung ${scenarioNameOf(r)} löschen`}
                          onClick={() => deleteRun(r)}
                        >
                          <Icon name="trash" size={15} />
                        </button>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {view === 'scenarios' ? (
            <footer className="picker-footer">
              <button type="button" className="button" onClick={importFile}>
                <Icon name="upload" size={16} />
                Importieren
              </button>
              <span>Importe gelten bis zum Neuladen</span>
            </footer>
          ) : null}
        </section>
      ) : null}
      {selected && !view ? (
        <section className="mission-briefing" aria-label="Szenariovorschau">
          <div className="briefing-meta">
            <span>{selected.situation.keyword || 'Neue Lage'}</span>
            <span>
              {MODE_LABELS[selected.mode]}
              {isExample ? ' · Beispiel' : ''}
              {selectedOrigin ? ` · ${selectedOrigin}` : ''}
            </span>
          </div>
          <h1>{selected.name}</h1>
          {selected.alert?.address ? (
            <p className="location-line">
              <Icon name="pin" size={15} />
              {selected.alert.address}
            </p>
          ) : null}
          <p className="briefing-text">
            {selected.situation.text ||
              'Bearbeite das Szenario, um die Ausgangslage zu beschreiben.'}
          </p>
          <div className="briefing-facts">
            <span>
              <Icon name="people" size={16} />
              {selected.patients.length} Patienten
            </span>
            <span>
              <Icon name="truck" size={16} />
              {selected.units.length} Kräfte
            </span>
            <span>
              <Icon name="bolt" size={16} />
              {selected.injects.length} Einspieler
            </span>
          </div>
          <div className="briefing-actions">
            <button type="button" className="button primary" onClick={() => begin(selected)}>
              <Icon name="play" size={17} />
              Übung starten
            </button>
            {isAdmin ? (
              <button
                type="button"
                className="button"
                onClick={() => openEditor(selected, serverById.get(selected.id)?.published)}
              >
                <Icon name="edit" size={16} />
                Bearbeiten
              </button>
            ) : null}
            {canDelete ? (
              <button
                type="button"
                className="button quiet icon-button danger-subtle"
                aria-label="Szenario löschen"
                onClick={() => deleteSelected(selected)}
              >
                <Icon name="trash" size={15} />
              </button>
            ) : null}
          </div>
          {latestRun ? (
            <button type="button" className="briefing-resume" onClick={() => resume(latestRun)}>
              <Icon name="history" size={16} />
              <span>
                Letzte Übung fortsetzen<strong>{scenarioNameOf(latestRun)}</strong>
              </span>
              <Icon name="chevron" size={16} />
            </button>
          ) : null}
        </section>
      ) : null}
      <div className="launch-map-note">
        <Icon name="cursor" size={14} />
        Karte ziehen und mit dem Mausrad zoomen
      </div>
    </div>
  );
}
